/**
 * Auth permission action hooks for document state management
 *
 * These hooks provide a Zustand-style API for auth permission actions.
 * The auth state is now fully managed by the Zustand AuthPermissionsStore.
 */
import {useFluxServices} from "injection/hooks";

import type {UseAuthPermissionsStore} from "modules/auth/state/permissions";

/**
 * Hook to get the setEditPermission action
 *
 * This action sets the edit permission in the Zustand AuthPermissionsStore.
 *
 * @returns A function to set the edit permission
 */
export function useSetEditPermission(): (hasEditPermission: boolean) => void {
    const {useAuthPermissionsStore} = useFluxServices();
    return useAuthPermissionsStore((state) => state.setCurrentUserHasEditPermission);
}

/**
 * Hook to get the setCommentingPermission action
 *
 * This action sets the commenting permission in the Zustand AuthPermissionsStore.
 *
 * @returns A function to set the commenting permission
 */
export function useSetCommentingPermission(): (hasCommentingPermission: boolean) => void {
    const {useAuthPermissionsStore} = useFluxServices();
    return useAuthPermissionsStore((state) => state.setCurrentUserHasCommentingPermission);
}

/**
 * Factory function to create auth permission actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch auth permission actions.
 *
 * @param useAuthPermissionsStore - The Zustand auth permissions store
 * @returns An object with auth permission action methods
 */
export function createAuthPermissionActions(useAuthPermissionsStore: UseAuthPermissionsStore) {
    return {
        setEditPermission: (hasEditPermission: boolean) => {
            useAuthPermissionsStore.getState().setCurrentUserHasEditPermission(hasEditPermission);
        },
        setCommentingPermission: (hasCommentingPermission: boolean) => {
            useAuthPermissionsStore.getState().setCurrentUserHasCommentingPermission(hasCommentingPermission);
        },
    };
}
